/**
 * useMakeTaskFromSignal — the ONE "signal → task" edge for the Two-Box Today UI (TASK-575).
 *
 * A signal (an alert ping, a radar change event) is noticed/dismissed, never "done". The only
 * bridge to the action spine is an explicit make-task click, which persists a real `tasks` row
 * via POST /api/tasks (the same path everything reads) tagged with the originating `source` +
 * a back-reference, so the DO column (which invalidates on create) picks it up immediately.
 */

import { toast } from 'sonner';
import { useCreateTask } from './useTasksQuery';
import { useAccount } from '../contexts/AccountContext';
import { buildTaskRequest } from '../utils/buildTaskRequest';
import type { TaskPriority } from '../types/tasks';

const DAY_MS = 24 * 60 * 60 * 1000;

export interface SignalTaskInput {
  title: string;
  /** Originating signal kind — becomes tasks.source (alert, drift, news, cio, dna, …). */
  source: string;
  clientId?: string;
  description?: string;
  priority?: TaskPriority;
  relatedEntityType?: string;
  relatedEntityId?: string;
  /**
   * TASK-724: the DURABLE binding. The Radar used to send a `change_events` row id, and that
   * projection is destroyed and rebuilt every 15 minutes — the back-reference dangled almost
   * immediately (8 such tasks measured live, and ZERO tasks ever referenced an alert).
   * `subjectKey` is derived from durable alert fields and survives every rebuild.
   */
  subjectKey?: string;
  subjectSourceEvent?: string;
  /** The Radar group the work was raised from (TASK-747). */
  originGroupKey?: string;
  /** The alert to navigate to. NOT the only one — see `contributingAlertIds`. */
  primaryAlertId?: string;
  /**
   * Every live alert id for the subject at creation time. CIO and DNA share `isin:{isin}`,
   * so keeping only one id meant completing work on one occurrence silently abandoned the
   * other, and the signal returned on the next tick (TASK-747).
   */
  contributingAlertIds?: string[];
  /**
   * TASK-761: which work-creation disposition this is. The SERVER validates it against the
   * signal action policy (an escalation's rationale travels as the description). Defaults
   * to `assign`.
   */
  dispositionIntent?: 'assign' | 'escalate';
}

export function useMakeTaskFromSignal() {
  const createTask = useCreateTask();
  const { user } = useAccount();

  return (input: SignalTaskInput) => {
    const req = buildTaskRequest({
      title: input.title,
      description: input.description,
      task_type: 'follow_up_other',
      priority: input.priority,
      due_date: new Date(Date.now() + 7 * DAY_MS).toISOString(),
      client_id: input.clientId,
      source: input.source,
      related_entity_type: input.relatedEntityType,
      related_entity_id: input.relatedEntityId,
      assignee_user_id: user?.id,
    });
    // Threaded outside `buildTaskRequest` so its existing contract is untouched.
    const withSubject = {
      ...req,
      ...(input.subjectKey ? { subject_key: input.subjectKey } : {}),
      ...(input.subjectSourceEvent ? { subject_source_event: input.subjectSourceEvent } : {}),
      ...(input.originGroupKey ? { origin_group_key: input.originGroupKey } : {}),
      ...(input.primaryAlertId ? { primary_alert_id: input.primaryAlertId } : {}),
      ...(input.contributingAlertIds?.length
        ? { contributing_alert_ids: input.contributingAlertIds }
        : {}),
      ...(input.dispositionIntent ? { disposition_intent: input.dispositionIntent } : {}),
    };
    createTask.mutate(withSubject, {
      onSuccess: () => toast.success('Task created'),
      onError: (e: unknown) => {
        // TASK-761: a policy refusal carries a structured {code, policy_id, reason} —
        // show the server's reason instead of a generic failure the RM cannot act on.
        const detail = (e as { response?: { data?: { detail?: { reason?: string } } } })
          ?.response?.data?.detail;
        toast.error(detail?.reason ?? 'Could not create task');
      },
    });
  };
}
